import { createAsyncThunk } from '@reduxjs/toolkit';

import { Tag, Task } from '@entities/task/model/types/tasks';
import { TasksInformationSchema } from '@entities/task/model/types/tasksInformationSchema';
import { $api } from '@shared/api';
import { formatDate } from '@shared/helpers';
import { IDataResponse } from '@shared/model/types/dataResponse.interface';

import { IAddModalSchemaState } from '../../modal/model/types/addModalSchema';
import { getUpdatedProjectsForFilter } from '../lib/getUpdatedProjectsForFilter/getUpdatedProjectsForFilter';
import { tasksInformationActions } from '../model/slice/tasksInformation';

interface FetchTasksError {
    message: string;
    errors: Array<{ title: string; source: { pointer: string } }>;
}

interface TaskPayload extends IAddModalSchemaState {}

export const createCustomTask = createAsyncThunk<
    any,
    TaskPayload,
    { rejectValue: FetchTasksError }
>('tasks/createCustomTask', async (taskPayload, thunkApi) => {
    // @ts-ignore
    const url = routable.getRoute('createTask');
    const {
        title,
        tags,
        date,
        color,
        description,
        estimateId,
        isCompleted,
        executorId,
        observerIds,
        projectId,
        status,
        attachments,
    } = taskPayload;

    const is_completed = isCompleted ? 1 : 0; // для бд нужно 1 или 0
    const formData = new FormData();

    const dateEndIsNotNull = !date || date.length === 1 ? '' : date;
    const formattedDate = formatDate(dateEndIsNotNull, 'YYYY-MM-DD');

    if (tags.length > 0) {
        tags.forEach((tag) => formData.append('tags[]', tag));
    }
    if (observerIds.length > 0) {
        observerIds.forEach((obs) => formData.append('observer_ids[]', JSON.stringify(obs)));
    }

    if (attachments.length > 0) {
        attachments.forEach((attach) => formData.append('new_attachments[]', attach.file));
    } else {
        formData.append('new_attachments[]', '');
    }

    // если исполнителя изменили на "не выбрано"
    const executorIdToFormData = executorId === null ? '' : (executorId as string);

    formData.append('title', title);
    formData.append('date', formattedDate);
    formData.append('color', color);
    formData.append('description', description);
    formData.append('estimate_id', !estimateId ? '' : (estimateId as string));
    formData.append('project_id', !projectId ? '' : (projectId as string));
    formData.append('is_completed', JSON.stringify(is_completed));
    formData.append('executor_id', executorIdToFormData);
    formData.append('status', status);

    try {
        const response = await $api.post<any>(`${url}`, formData);
        if (response.status !== 200 || response.data.status === 'error') {
            const errorMessage = response.data.message || 'Неизвестная ошибка';
            const errors = response.data.errors || [];
            return thunkApi.rejectWithValue({ message: errorMessage, errors });
        }

        const state = thunkApi.getState();

        // @ts-ignore
        const { projectsForFilter, projects } = state.tasksInformationReducer;

        const updatedProjectsForFilter = getUpdatedProjectsForFilter(
            projectId,
            estimateId,
            projectsForFilter,
            projects
        );
        if (updatedProjectsForFilter) {
            thunkApi.dispatch(
                tasksInformationActions.updateProjectsForFilter(updatedProjectsForFilter)
            );
        }

        return {
            task: response.data.data.task,
            newTabs: response.data.data.new_tabs,
            newTags: response.data.data.new_tags,
        };
    } catch (error) {
        const errorMessage = error.response?.data?.message || 'Неизвестная ошибка';
        const errors = error.response?.data?.errors || [];
        return thunkApi.rejectWithValue({ message: errorMessage, errors });
    }
});

interface ICreateCustomTaskWithoutThunk {
    title: string;
    description?: string;
    date?: string;
    executorId?: string;
}

export const createCustomTaskWithoutThunk = async ({
    title,
    description,
    date,
    executorId,
}: ICreateCustomTaskWithoutThunk) => {
    // @ts-ignore
    const url = routable.getRoute('createTask');

    const formData = new FormData();
    const dateEndIsNotNull = !date || date.length === 1 ? '' : date;
    const formattedDate = formatDate(dateEndIsNotNull, 'YYYY-MM-DD');

    formData.append('title', title);
    formData.append('date', formattedDate);
    formData.append('color', '#9DA1AC');
    formData.append('description', description);
    formData.append('estimate_id', '');
    formData.append('project_id', '');
    formData.append('is_completed', '0');
    formData.append('executor_id', executorId);
    formData.append('status', 'is_waiting');

    try {
        // eslint-disable-next-line camelcase
        const { data } = await $api.post<
            IDataResponse<{ new_tabs: TasksInformationSchema['tabs']; new_tags: Tag[]; task: Task }>
        >(`${url}`, formData);

        return data;
    } catch (error) {
        console.log('create custom task error: ', error);
        return error;
    }
};
